import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import styled from '@emotion/styled';
import { sortBy } from 'lodash';
import gql from 'graphql-tag';
import { useApolloClient, useQuery } from '@apollo/client';
import PredefinedMessages from '~/Components/PredefinedMessages';
import { CompleteBongQuery_venue_company_offers as Offer } from '~Scenes/POS/Queries/__queries__';
import { LocalItem } from '~Types';
import { useI18n } from '~/Hooks';
import Modal from '~ComponentsImproved/Modal';
import CartHeader from './Components/CartHeader';
import ItemRow from './Components/Item';
import OfferItemWrapper from './Components/OfferItemWrapper';
import ItemsWrapper from './Components/ItemsWrapper';
import BottomCartSection from './Components/BottomSection';
import { useStorage } from '~Components/Storage';
import { useCart } from '~Components/CartProvider';
import { v4 as uuid } from 'uuid';
import { combineArticles } from '~Utils/combineArticles';
import { Box } from '~Styles';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import TicketMessageRows from './Components/TicketMessageRow';
import { VenueData, VenueDataVariables } from './__queries__';
import { groupOfferMatches } from '~Utils/offers';

const DATA_QUERY = gql`
  query VenueData($id: ID!) {
    venue: Venue(id: $id) {
      id

      predefinedMessages {
        id
        message
        type
      }
    }
  }
`;

export const CartWrapper = styled.div`
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--colorBlueGray1300);
  box-shadow: 0px 0px 12px -3px rgba(0, 0, 0, 0.3);
  width: 326px;
  position: relative;
  z-index: 3;
  border-radius: 3px;
  display: flex;
  height: 100%;
  flex-direction: column;
  box-sizing: border-box;
`;

export const CashDrawerOpenInfo = styled(Box)`
  text-align: center;
  background-color: var(--colorUISand);
  color: var(--colorBlueGray1300);
  padding: 16px;
`;

interface Props {
  venueId: string;
  onClearCart: () => void;
  offers: Offer[];
  buttons: ReactNode;
  paymentId: string | null;
  tableId?: string;
  showMessageModal: boolean;
  setShowMessageModal: (val: boolean) => void;
  selectedHasVariations: boolean;
  showModifyModal: () => void;
  isTsr?: boolean;
}

const Cart = ({
  venueId,
  onClearCart,
  offers,
  buttons,
  paymentId,
  tableId,
  showMessageModal,
  setShowMessageModal,
  selectedHasVariations,
  showModifyModal,
  isTsr,
}: Props) => {
  const cart = useCart();
  const cashDrawer = useCashDrawer();

  const { i18n } = useI18n();
  const [storage] = useStorage();

  const { data } = useQuery<VenueData, VenueDataVariables>(DATA_QUERY, {
    variables: {
      id: venueId,
    },
    skip: !venueId,
  });

  const messages: string[] = [];
  if (data?.venue?.predefinedMessages) {
    data?.venue?.predefinedMessages.map(
      (item: any) => item.type === 'ITEM' && messages.push(item.message),
    );
  }

  const client = useApolloClient();
  const [changeItemCourseType, setChangeItemCourseType] = useState<string | null>(null);

  const serviceChargeItems = cart.items.filter((item) =>
    item.title.toLowerCase().includes('service charge'),
  );
  const serviceChargeItem = serviceChargeItems[0];

  useEffect(() => {
    if (tableId) {
      // Service charge is handled via table-events for TSR so below code is QSR specific
      return;
    }

    if (!serviceChargeItem) return;

    const chequeAmountWithoutServiceCharge =
      cart.total - cart.discount - (serviceChargeItem.price ?? 0);

    const findPercentageValue = /\d+(?:[.,]\d+)?\s*%/;
    const serviceChargePercentage = serviceChargeItem?.title?.match(findPercentageValue);

    if (!serviceChargePercentage || !serviceChargePercentage[0]) return;

    const formattedServiceChargePercentage = parseFloat(
      serviceChargePercentage[0].replace(',', '.'),
    );

    const serviceChargeAmount = Math.round(
      chequeAmountWithoutServiceCharge * (formattedServiceChargePercentage / 100),
    );

    // If the service charge item is already in the cheque and has the correct amount, do nothing
    if (serviceChargeItem.price === serviceChargeAmount) return;

    // If the service charge item is already in the cheque but has the wrong amount, remove it
    cart.removeItem(serviceChargeItem.id);

    // Add the service charge item again with the correct amount, prevent it being added multiple times
    if (serviceChargeItems.length <= 1) {
      cart.addItem({
        id: uuid(),
        categoryId: serviceChargeItem.categoryId,
        variations: [],
        removedVariations: [],
        vatRate: serviceChargeItem.vatRate,
        takeAwayVatRate: serviceChargeItem.takeAwayVatRate,
        discount: 0,
        description: '',
        fraction: 1,
        itemId: serviceChargeItem.itemId,
        title: serviceChargeItem.title,
        price: serviceChargeAmount,
        disableMembershipDiscount: serviceChargeItem.disableMembershipDiscount || false,
      });
    }
  }, [cart.total, cart.discount, serviceChargeItem?.id]);

  const grouped = useMemo(() => groupOfferMatches(cart.items, venueId, client, i18n), [
    cart.items,
    venueId,
    client,
    i18n,
  ]);

  const sortedItems = sortBy(grouped.unmatched, [
    ({ courseType }) => courseType?.order || Infinity,
  ]);

  const adjustedItems = storage.compressedCart ? combineArticles(sortedItems) : sortedItems;

  function renderItem(item: LocalItem, hidePrice?: boolean) {
    const entry = cart.packagedItems.find((i) => i.id === item.id);

    if (!entry) {
      return null;
    }

    return (
      <ItemRow
        key={item.id}
        item={{ ...item, entry }}
        changeCourseType={(id) => setChangeItemCourseType(id)}
        hidePrice={hidePrice}
      />
    );
  }

  return (
    <CartWrapper>
      <CartHeader tableId={tableId} onClearCart={onClearCart} hideServiceType={isTsr} />

      <ItemsWrapper
        changeItemCourseType={changeItemCourseType}
        closeCourseChanger={() => setChangeItemCourseType(null)}>
        {adjustedItems.map((item) => renderItem(item))}
        {grouped.matches.map((offerMatch, index) => {
          const offerPrice = offerMatch.items.reduce(
            (acc, item) => {
              const entry = cart.packagedItems.find((i) => i.id === item.id);
              if (!entry) return acc;

              acc.oldPrice += entry.amount;
              acc.newPrice += entry.amount - (entry.discount ?? 0);

              return acc;
            },
            {
              oldPrice: 0,
              newPrice: 0,
            },
          );

          return (
            <OfferItemWrapper
              key={index}
              offerId={offerMatch.offerId}
              oldPrice={offerPrice.oldPrice}
              newPrice={offerPrice.newPrice}>
              {offerMatch.items
                .map(({ id }) => cart.items.find((i) => i.id === id)!)
                .map((item) => renderItem(item, true))}
            </OfferItemWrapper>
          );
        })}

        <TicketMessageRows />
      </ItemsWrapper>

      {cashDrawer.isOpen ? (
        <CashDrawerOpenInfo>
          <Paragraph level={3} color="var(--colorBlyeGray1200)">
            {i18n('Cashdrawer.BlockedPayments')}
          </Paragraph>
        </CashDrawerOpenInfo>
      ) : (
        <BottomCartSection
          venueId={venueId}
          offers={offers}
          paymentId={paymentId}
          showMessageModal={showMessageModal}
          setShowMessageModal={setShowMessageModal}
          isTsr={isTsr}
          selectedHasVariations={selectedHasVariations}
          showModifyModal={showModifyModal}
          buttons={buttons}
        />
      )}

      {showMessageModal && (
        <Modal isShowing={showMessageModal} hide={() => setShowMessageModal(!showMessageModal)}>
          <PredefinedMessages
            onSubmit={(value) => {
              cart.selectedItems.setMessage(typeof value === 'string' ? value : value.label);
              cart.selectedItems.clear();

              setShowMessageModal(false);
            }}
            value={cart.selectedItems.items.find((i) => i.message)?.message}
            messages={messages}
            width={500}
          />
        </Modal>
      )}
    </CartWrapper>
  );
};

export default React.memo(Cart);
